import React from 'react'
import { SignatureComponent } from '@syncfusion/ej2-react-inputs'
import { Modal } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import ConfirmationModal from '@components/common/ConfirmationModal/ConfirmationModal.component'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import CustomTemplateModal from '../CustomTemplateModal.component'
import NotesSaveNoteModal from '@components/pages/Notes/Modals/NotesSaveNoteModal.component'
import NotesUnsavedChangesModal from '@components/pages/Notes/Modals/NotesUnsavedChangesModal.component'
import type { INote } from '@dataTypes/note.type'
import type { CustomTemplate } from './noteEditor.types'

type NoteEditorModalsProps = {
  showResetRecordingModal: boolean
  onCancelResetRecording: () => void
  onConfirmResetRecording: () => void
  showCustomTemplateModal: boolean
  onCloseCustomTemplate: () => void
  onCustomTemplateSubmit: (payload: {
    name: string
    content: string
    templateId?: string
    existingTemplateId?: string
  }) => void | Promise<void>
  onCustomTemplateDelete: (templateId: string) => void | Promise<void>
  editingTemplateId: string | null
  customTemplates: CustomTemplate[]
  showSuccessModal: boolean
  noteTitle: string
  effectiveTag: string
  isSavingNote: boolean
  onDiscardSave: () => void
  onConfirmSave: () => void
  displayedNote: INote | null
  onDelete?: (noteId: string) => void
  hasContextId: boolean
  showDeleteModal: boolean
  onHideDelete: () => void
  onConfirmDelete: () => void
  showSignaturePad: boolean
  signatureRef: React.RefObject<SignatureComponent>
  onSignatureCancel: () => void
  onSignatureSave: () => void
  showEmptyNoteWarning: boolean
  onEmptyNoteGotIt: () => void
  onEmptyNoteDiscard: () => void
  showUnsavedModal: boolean
  unsavedLeaveMessage: string
  onUnsavedDismiss: () => void
  onUnsavedDiscard: () => void
  onUnsavedSave: () => void
}

const NoteEditorModals = ({
  showResetRecordingModal,
  onCancelResetRecording,
  onConfirmResetRecording,
  showCustomTemplateModal,
  onCloseCustomTemplate,
  onCustomTemplateSubmit,
  onCustomTemplateDelete,
  editingTemplateId,
  customTemplates,
  showSuccessModal,
  noteTitle,
  effectiveTag,
  isSavingNote,
  onDiscardSave,
  onConfirmSave,
  displayedNote,
  onDelete,
  hasContextId,
  showDeleteModal,
  onHideDelete,
  onConfirmDelete,
  showSignaturePad,
  signatureRef,
  onSignatureCancel,
  onSignatureSave,
  showEmptyNoteWarning,
  onEmptyNoteGotIt,
  onEmptyNoteDiscard,
  showUnsavedModal,
  unsavedLeaveMessage,
  onUnsavedDismiss,
  onUnsavedDiscard,
  onUnsavedSave,
}: NoteEditorModalsProps): JSX.Element => (
  <>
    <Modal
      show={showResetRecordingModal}
      onHide={onCancelResetRecording}
      centered
      className="notes-reset-recording-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Reset recording</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="reset-recording-text">
          Resetting will clear the current audio recording and remove any
          unsaved summary.
        </p>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button
          size="md"
          className="button-secondary"
          onClick={onCancelResetRecording}
        >
          Cancel
        </Button>
        <Button
          size="md"
          className="button-primary"
          onClick={onConfirmResetRecording}
        >
          Reset
        </Button>
      </Modal.Footer>
    </Modal>
    <CustomTemplateModal
      isOpen={showCustomTemplateModal}
      onClose={onCloseCustomTemplate}
      onSubmit={onCustomTemplateSubmit}
      onDelete={onCustomTemplateDelete}
      initialTemplate={
        editingTemplateId
          ? (customTemplates.find((t) => t.id === editingTemplateId) ?? null)
          : null
      }
      backendTemplates={customTemplates}
    />
    <NotesSaveNoteModal
      show={showSuccessModal}
      noteName={noteTitle}
      tag={effectiveTag}
      isSaving={isSavingNote}
      onDiscard={onDiscardSave}
      onSave={onConfirmSave}
    />
    {displayedNote && (onDelete || hasContextId) && (
      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={onHideDelete}
        onConfirm={() => {
          onHideDelete()
          onConfirmDelete()
        }}
        entityName={noteTitle}
      />
    )}
    {showSignaturePad && (
      <div className="signature-modal-overlay">
        <div className="signature-modal">
          <div className="signature-modal-header">
            <h3>Add Signature</h3>
            <button className="signature-close-btn" onClick={onSignatureCancel}>
              <i className="mgc mgc_close_line" />
            </button>
          </div>
          <div className="signature-modal-body">
            <SignatureComponent
              ref={signatureRef}
              id="signature"
              backgroundColor="transparent"
              strokeColor="#000000"
              minStrokeWidth={1}
              maxStrokeWidth={3}
              velocity={0.7}
            />
          </div>
          <div className="signature-modal-footer">
            <Button
              size="md"
              className="button-secondary"
              onClick={onSignatureCancel}
            >
              Cancel
            </Button>
            <Button
              size="md"
              className="button-primary"
              onClick={onSignatureSave}
            >
              Save Signature
            </Button>
          </div>
        </div>
      </div>
    )}
    <ConfirmationModal
      show={showEmptyNoteWarning}
      onHide={onEmptyNoteGotIt}
      title="Empty note"
      backdropClassName="note-overlay-confirmation-backdrop"
      className="notes-empty-warning-modal"
      headerClassName="notes-confirmation-modal-header"
      size="md"
      body={
        <div className="notes-confirmation-modal__body">
          <p className="notes-confirmation-modal__message">
            Please add content to your note before saving.
          </p>
        </div>
      }
      footer={
        <div className="notes-confirmation-modal__footer">
          <Button
            size="md"
            className="button-secondary"
            onClick={onEmptyNoteDiscard}
          >
            Discard
          </Button>
          <Button
            size="md"
            className="button-primary"
            onClick={onEmptyNoteGotIt}
          >
            Okay
          </Button>
        </div>
      }
    />
    <NotesUnsavedChangesModal
      show={showUnsavedModal}
      leaveMessage={unsavedLeaveMessage}
      onDismiss={onUnsavedDismiss}
      onDiscard={onUnsavedDiscard}
      onSave={onUnsavedSave}
    />
  </>
)

export default NoteEditorModals
